import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLoading } from '../context/LoadingContext';

const Loading = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const { setIsLoading: setGlobalLoading } = useLoading();

  const MIN_LOADING_TIME = 1500; // Minimum 1.5 seconds

  // Show video after minimum loading time
  useEffect(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
    
    const timer = setTimeout(() => {
      // Video will start playing automatically via autoplay
    }, remainingTime);

    return () => clearTimeout(timer);
  }, []);

  // Prevent scrolling while loading
  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLoading]);

  // Handle video end
  const handleVideoEnd = () => {
    setVideoEnded(true);
    setTimeout(() => {
      setIsLoading(false);
      setGlobalLoading(false);
    }, 500);
  };

  // Handle video error - if video fails to load, just hide loading
  const handleVideoError = () => {
    setVideoEnded(true);
    setTimeout(() => {
      setIsLoading(false);
      setGlobalLoading(false);
    }, 500);
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.div
          key="loading-container"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black"
        >
          <motion.div
            key="video"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: videoEnded ? 0 : 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            className="w-full h-full flex items-center justify-center"
          >
            <video
              ref={videoRef}
              className="max-w-2xl max-h-[60vh] w-auto h-auto object-contain"
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              onError={handleVideoError}
            >
              <source src="/videos/loading screen.mp4" type="video/mp4" />
            </video>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loading;

